/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/ReliabilityStandards/AdminReliabilityStandards.tsx.
 */

import React, { useState, useEffect } from "react";
import { DropdownOption } from "../../../../interfaces/dropdownInterfaces";
import {
  reliabilityStandardsService,
  ReliabilityStandardConfig,
} from "../../../../services/reliabilityStandardsService";
import { useReliabilityDropdowns } from "../../User/MaterialAnalysisInput/hooks/useReliabilityDropdowns";
import "../Admin.css";
import "./AdminReliabilityStandards.css";

const AdminReliabilityStandards: React.FC = () => {
  const [config, setConfig] = useState<ReliabilityStandardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [dataReviewValue, setDataReviewValue] = useState<string>("");
  const [dataCompletenessValue, setDataCompletenessValue] = useState<string>("");
  const [dataAcquisitionValue, setDataAcquisitionValue] = useState<string>("");

  // Get dropdown options
  const {
    dataReviewOptions,
    dataCompletenessOptions,
    dataAcquisitionOptions,
    loading: dropdownsLoading,
  } = useReliabilityDropdowns();

  // Fetch current active configuration
  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await reliabilityStandardsService.getActive();
        if (response.success && response.data) {
          setConfig(response.data);
          setDataReviewValue(response.data.dataReviewValue);
          setDataCompletenessValue(response.data.dataCompletenessValue);
          setDataAcquisitionValue(response.data.dataAcquisitionValue);
        } else {
          setError(response.message || "No active configuration found");
        }
      } catch (err: any) {
        console.error("Failed to fetch reliability standards:", err);
        setError(err.response?.data?.message || "Failed to load reliability standards");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Helper function to get dropdown option value
  const dropdownOptionValue = (option: DropdownOption): string =>
    String(option.value ?? option.id);

  // Helper function to resolve dropdown label
  const resolveDropdownLabel = (option: DropdownOption): string =>
    option.label ?? dropdownOptionValue(option);

  // Handle save/update
  const handleSave = async () => {
    if (!dataReviewValue || !dataCompletenessValue || !dataAcquisitionValue) {
      setError("All fields are required");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let response;
      if (config) {
        // Update existing configuration
        response = await reliabilityStandardsService.update(config.id, {
          dataReviewValue,
          dataCompletenessValue,
          dataAcquisitionValue,
          isActive: true, // Ensure it stays active
        });
      } else {
        // Create new configuration
        response = await reliabilityStandardsService.create({
          dataReviewValue,
          dataCompletenessValue,
          dataAcquisitionValue,
        });
      }

      if (response.success && response.data) {
        setConfig(response.data);
        setSuccessMessage(
          response.message || "Reliability standards updated successfully"
        );
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(response.message || "Failed to update reliability standards");
      }
    } catch (err: any) {
      console.error("Failed to save reliability standards:", err);
      setError(
        err.response?.data?.message || "Failed to save reliability standards"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || dropdownsLoading) {
    return (
      <div className="background-form-container analysis-shell">
        <div className="analysis-banner">
          <span className="analysis-banner__title">Reliability Standards</span>
        </div>
        <div className="admin-reliability-standards">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading reliability standards...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Reliability Standards</span>
      </div>
      <div className="admin-form-root">
        <div className="admin-reliability-standards dataset-container">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}
          {successMessage && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              <span>{successMessage}</span>
            </div>
          )}

          <div className="dataset-section">
            <h3 className="dataset-section-title">ICoMEC Data Reliability Standards</h3>
            <p style={{ marginBottom: 16, color: "#555", fontSize: "14px" }}>
              Data Reliability standards refer to ICoMEC&apos;s default standards for data quality, based on:
              Data Review, Data Completeness, and Data Acquisition. Together, these help filter &apos;reliable&apos; data for your study.
            </p>
            <div className="dataset-grid">
              <div className="form-field">
                <label>Data Review *</label>
                <select
                  value={dataReviewValue}
                  onChange={(e) => setDataReviewValue(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Data Review</option>
                  {dataReviewOptions.map((option: DropdownOption) => (
                    <option key={option.id} value={dropdownOptionValue(option)}>
                      {resolveDropdownLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Data Completeness *</label>
                <select
                  value={dataCompletenessValue}
                  onChange={(e) => setDataCompletenessValue(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Data Completeness</option>
                  {dataCompletenessOptions.map((option: DropdownOption) => (
                    <option key={option.id} value={dropdownOptionValue(option)}>
                      {resolveDropdownLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Data Acquisition *</label>
                <select
                  value={dataAcquisitionValue}
                  onChange={(e) => setDataAcquisitionValue(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Data Acquisition</option>
                  {dataAcquisitionOptions.map((option: DropdownOption) => (
                    <option key={option.id} value={dropdownOptionValue(option)}>
                      {resolveDropdownLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {config && (
              <div className="config-info" style={{ marginTop: 12, padding: "8px 0", fontSize: "13px", color: "#666" }}>
                Created: {new Date(config.createdAt).toLocaleDateString()} | Last Updated: {new Date(config.updatedAt).toLocaleDateString()}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="apply-filter-btn"
                onClick={handleSave}
                disabled={saving || !dataReviewValue || !dataCompletenessValue || !dataAcquisitionValue}
              >
                {saving ? "Saving..." : config ? "Update Configuration" : "Create Configuration"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** React component — Admin Reliability Standards. */
export default AdminReliabilityStandards;

